import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { createApiClient, apiClient, getLocalTimestamp } from "../../api/apiClient";
import { setEmployees } from "./appDataSlice";
import { RootState } from "..";
import { handleEmployeeUpdateErrorWithFriendlyMessage, EMPLOYEE_UPDATE_API_NAMES } from '../utils/employeeUpdateErrorHandler';

// API Response interface
export interface EmployeeApiResponse {
  ID: number;
  Location_ID: number;
  Location_Name: string;
  GPID: string;
  First_Name: string;
  Last_Name: string;
  Role: string;
  Status: string; // Added Status field
  Active_vs_Inactive: string;
  Created: string;
  Modified: string;
  GUID: string;
  Last_Pulse_Check: string;
  Last_Observation: string;
  Week: number;
  Period: number;
  Current_Week: number;
  Peer_Coach: string;
  New_Hire: number; // 0 or 1
  Termination_Date: string;
  Hire_Date: string;
  Joining_Date: string;
  Expected_Graduation_Date: string;
  Weeks_till_graduation: number;
  Manager: string;
  Last_Observation_Date: string;
  Last_Pulse_Check_Date: string;
  Job_Description: string; // Added Job_Description field
}

export interface EmployeeUpdateError {
  id: string;
  message: string;
  details?: string;
  apiName?: string;
  timestamp?: number;
  // optional context to store originalMessage and other debug info (used by error overlays)
  context?: any;
}

// Thunk to fetch employees from API
export const fetchEmployees = createAsyncThunk(
  "employeeUpdates/fetchEmployees",
  async (
    params: { location: string; filters?: Record<string, string>; },
    { rejectWithValue, dispatch }
  ) => {
    try {
      // Create API client
      const api = createApiClient();

      // Build base URL with required location parameter
      // Correct API path 'nho' (not 'no')
      let url = `/nho/employees?Location_Name=${encodeURIComponent(
        params.location
      )}&clientTimestamp=${getLocalTimestamp()}`;

      console.log(`A. URL before filters: ${url}`);
      // Add optional filter parameters if provided
      if (params.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          // Ensure value is defined, not null or empty, and not 'All' (case insensitive)
          if (value && value !== "" && value.toLowerCase() !== "all") {
            url += `&${key}=${encodeURIComponent(value)}`;
          }
        });
      }
      console.log(`B. Final API URL: ${url}`);
      // Make API call
      const response = await apiClient(api, url, "GET");

      // Format a date string to mm/dd/yyyy
      const formatDate = (dateStr: string | null | undefined): string => {
        if (!dateStr) {
          return "N/A";
        }
        try {
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) {
            return "N/A";
          }

          // Format as mm/dd/yyyy
          const month = (date.getMonth() + 1).toString().padStart(2, "0");
          const day = date.getDate().toString().padStart(2, "0");
          const year = date.getFullYear();

          return `${month}/${day}/${year}`;
        } catch (error) {
          return "N/A";
        }
      };

      // Map API response to Employee type for employeeUpdates slice
      const employeeUpdatesData = response.map((emp: EmployeeApiResponse) => ({
        id: emp.ID,
        firstName: emp.First_Name || "",
        lastName: emp.Last_Name || "",
        gpid: emp.GPID || "",
        status: emp.Status || "", // Map from Status field
        jobDescription: emp.Job_Description || "", // Map from Job_Description field
        lastObservation: formatDate(emp.Last_Observation), // Format to mm/dd/yyyy
        lastPulseCheck: formatDate(emp.Last_Pulse_Check), // Format to mm/dd/yyyy
        hireDate: formatDate(emp.Hire_Date), // Map from Hire_Date and format
        graduationDate: formatDate(emp.Expected_Graduation_Date), // Map from Expected_Graduation_Date and format
        terminationDate: formatDate(emp.Termination_Date), // Map from Termination_Date and format
        manager: emp.Manager || "",
        newHire: emp.New_Hire === 1,
        isDeleted: false,
      }));

      // Map API response to appData.employees format
      const appDataEmployees = response.map((emp: EmployeeApiResponse) => ({
        Id: emp.ID,
        Location_ID: emp.Location_ID || parseInt(params.location),
        GPID: emp.GPID || "",
        First_Name: emp.First_Name || "",
        Last_Name: emp.Last_Name || "",
        Role: emp.Job_Description || emp.Role || "",
        Active_vs_Inactive: emp.Active_vs_Inactive || emp.Status || "",
        Created: emp.Created || getLocalTimestamp(),
        Modified: emp.Modified || getLocalTimestamp(),
        Last_Pulse_Check: emp.Last_Pulse_Check || null,
        Last_Observation: emp.Last_Observation || null,
        New_Hire: emp.New_Hire || 0,
        Termination_Date: emp.Termination_Date || null,
        Hire_Date: emp.Hire_Date || null,
        Joining_Date: emp.Joining_Date || null,
        Expected_Graduation_Date: emp.Expected_Graduation_Date || null,
        Weeks_till_graduation: emp.Weeks_till_graduation || null,
        Manager: emp.Manager || null,
        Last_Observation_Date: emp.Last_Observation_Date || null,
        Last_Pulse_Check_Date: emp.Last_Pulse_Check_Date || null,
        Last_Observation_PeriodWeek: null, // Not available in API response
        Last_Pulse_Check_PeriodWeek: null, // Not available in API response
      }));

      // Dispatch to appData.employees store with proper structure
      dispatch(setEmployees({ data: appDataEmployees }));

      // Return data for employeeUpdates slice
      return employeeUpdatesData;
    } catch (error: any) {
      try {
        const centralizedError = handleEmployeeUpdateErrorWithFriendlyMessage(
          EMPLOYEE_UPDATE_API_NAMES.FETCH,
          error,
          true,
          { location: params.location }
        );
        return rejectWithValue(centralizedError.message || "Failed to fetch employees");
      } catch (handlerErr) {
        console.error('Failed to dispatch centralized fetch error:', handlerErr);
        return rejectWithValue(error.message || "Failed to fetch employees");
      }
    }
  }
);

// Thunk to create new employee via API
export const createEmployee = createAsyncThunk(
  "employeeUpdates/createEmployee",
  async (
    dates: {
      Hire_Date: string | null;
      Expected_Graduation_Date: string | null;
    } = { Hire_Date: null, Expected_Graduation_Date: null },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as {
        employeeUpdates: EmployeeUpdatesState;
        appData: {
          selectedLocation: {
            id: any;
            displayText: string;
          };
        };
      };
      const {
        firstName,
        lastName,
        gpid,
        selectedStatus,
        jobDescription,
        manager,
      } = state.employeeUpdates;
      const location = state.appData?.selectedLocation?.displayText;
      const locationId = state.appData?.selectedLocation?.id;

      // Validate required fields
      if (!firstName || !firstName.trim()) {
        return rejectWithValue("First Name is required");
      }

      if (!lastName || !lastName.trim()) {
        return rejectWithValue("Last Name is required");
      }

      if (!gpid || !gpid.trim()) {
        return rejectWithValue("GPID is required");
      }

      if (!selectedStatus) {
        return rejectWithValue("Status is required");
      }

      if (!jobDescription || !jobDescription.trim()) {
        return rejectWithValue("Job Description is required");
      }

      if (!manager || !manager.trim()) {
        return rejectWithValue("Manager is required");
      }

      if (!location || !location.trim()) {
        return rejectWithValue("Location is required");
      }

      // Validate mandatory dates
      if (!dates.Hire_Date) {
        return rejectWithValue("Hire Date is required");
      }

      if (!dates.Expected_Graduation_Date) {
        return rejectWithValue("Graduation Date is required");
      }

      // Prepare request body
      const currentDate = getLocalTimestamp()
      const requestBody = {
        First_Name: firstName.trim(),
        Last_Name: lastName.trim(),
        GPID: gpid.trim(),
        Status: selectedStatus.displayText,
        Job_Description: jobDescription.trim(),
        Manager: manager.trim(),
        Location_Name: location.trim(),
        Location_ID: locationId ?? null,
        Hire_Date: dates.Hire_Date,
        Expected_Graduation_Date: dates.Expected_Graduation_Date,
        Created: currentDate,
        Modified: currentDate,
      };

      // Create API client
      const api = createApiClient();

      // Make API call
      const response = await apiClient(
        api,
        "/nho/employees",
        "POST",
        requestBody
      );

      // Return the response
      return response;
    } catch (error: any) {
      // Provide a more detailed error message from the API response if available
      try {
        const centralizedError = handleEmployeeUpdateErrorWithFriendlyMessage(
          EMPLOYEE_UPDATE_API_NAMES.CREATE,
          error,
          true
        );
        return rejectWithValue(centralizedError.message || "Failed to create employee. Please try again.");
      } catch (handlerErr) {
        console.error("API Error in createEmployee:", error);
        return rejectWithValue(
          error.response?.data?.message || error.response?.data?.error || error.message || "Failed to create employee. Please try again."
        );
      }
    }
  }
);

// Thunk to lookup employee by GPID
export const lookupEmployeeByGpid = createAsyncThunk(
  "employeeUpdates/lookupEmployeeByGpid",
  async (gpid: string, { rejectWithValue }) => {
    try {
      // Validate GPID format (8 digits)
      if (!gpid || gpid.length !== 8 || !/^\d{8}$/.test(gpid)) {
        return rejectWithValue("Invalid GPID format");
      }

      // Create API client with special handling for GPID lookup
      const api = createApiClient();
      
      // Add a flag to the request config to indicate this is a GPID lookup
      // This will help prevent automatic logout redirects for this specific API call
      const requestConfig = {
        url: `/common/employees/gpid/${gpid}`,
        method: "GET" as const,
        metadata: {
          isGpidLookup: true, // Flag to identify GPID lookup requests
        }
      };

      // Make API call to lookup employee by GPID
      const response = await api(requestConfig);

      // Return the employee data
      return response.data;
    } catch (error: any) {
      // Special handling for 401 errors on GPID lookup
      if (error.response?.status === 401) {
        console.log("GPID lookup returned 401 - employee not found or unauthorized");
        return rejectWithValue("Employee not found with this GPID");
      }
      
      // Handle other API errors
      try {
        const centralizedError = handleEmployeeUpdateErrorWithFriendlyMessage(
          EMPLOYEE_UPDATE_API_NAMES.LOOKUP_GPID,
          error,
          true,
          { gpid }
        );
        return rejectWithValue(centralizedError.message || "Failed to lookup employee by GPID");
      } catch (handlerErr) {
        const errorMessage = error.response?.data?.message || error.response?.data?.error_msg || error.response?.data?.error || error.message || "Failed to lookup employee by GPID";
        console.log("GPID lookup failed:", errorMessage);
        return rejectWithValue(errorMessage);
      }
    }
  }
);

// Thunk to delete an employee via API
export const deleteEmployee = createAsyncThunk(
  "employeeUpdates/deleteEmployee",
  async (id: number, { rejectWithValue }) => {
    try {
      // Create API client
      const api = createApiClient();

      // Enhanced logging to debug the ID issue
      console.log("Deleting employee - ID type:", typeof id);
      console.log("Deleting employee - ID value:", id);

      // Ensure ID is valid
      if (id === undefined || id === null) {
        console.error("Invalid ID provided for deletion:", id);
        return rejectWithValue({
          success: false,
          message: "Cannot delete employee with invalid ID",
        });
      }

      // Build URL with employee internal ID
      const url = `/nho/employees/${id}`;
      console.log("DELETE request URL:", url);

      // Make DELETE API call
      const response = await apiClient(api, url, "DELETE");

      // Return success response with internal ID
      return {
        success: true,
        id: id, // Using the internal ID consistently
        message: response.message || "Employee deleted successfully.",
      };
    } catch (error: any) {
      // Handle error response
      let errorMessage = "Failed to delete employee";

      if (typeof error === "string") {
        errorMessage = error;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error && typeof error === "object") {
        if ("message" in error) {
          errorMessage = String(error.message);
        } else if (
          "data" in error &&
          typeof error.data === "object" &&
          error.data &&
          "message" in error.data
        ) {
          errorMessage = String(error.data.message);
        } else if (
          "response" in error &&
          typeof error.response === "object" &&
          error.response &&
          "data" in error.response
        ) {
          const responseData = error.response.data;
          if (
            responseData &&
            typeof responseData === "object" &&
            "message" in responseData
          ) {
            errorMessage = String(responseData.message);
          }
        }
      }

      try {
        const centralizedError = handleEmployeeUpdateErrorWithFriendlyMessage(
          EMPLOYEE_UPDATE_API_NAMES.DELETE,
          error,
          true,
          { id }
        );
        return rejectWithValue({
          success: false,
          message: centralizedError.message || errorMessage,
        });
      } catch (handlerErr) {
        console.error('Failed to dispatch centralized delete error:', handlerErr);
        return rejectWithValue({ success: false, message: errorMessage });
      }
    }
  }
);

// Thunk to update an employee via API
export const updateEmployee = createAsyncThunk(
  "employeeUpdates/updateEmployee",
  async (employee: Employee, { rejectWithValue, getState, dispatch }) => {
    try {
      // Create API client
      const api = createApiClient();
      
      // Get the current state to access selectedLocation
      const state = getState() as RootState;
      const { selectedLocation } = state.appData;

      // Enhanced logging to debug the employee data issue
      console.log("Updating employee - Raw data:", JSON.stringify(employee));

      // Ensure we have a valid employee object
      if (!employee || typeof employee !== "object") {
        console.error("Invalid employee data:", employee);
        return rejectWithValue({
          success: false,
          message: "Invalid employee data provided",
        });
      }

      // Explicitly verify ID exists and is a number
      const id = employee.id;
      console.log("Employee ID type:", typeof id);
      console.log("Employee ID value:", id);

      // More strict validation on ID
      if (id === undefined || id === null || isNaN(Number(id))) {
        console.error("Missing or invalid employee ID:", id);
        return rejectWithValue({
          success: false,
          message: "Cannot update employee without a valid internal ID",
        });
      }

      // Build URL with employee internal ID - ensure it's a number
      const url = `/nho/employees/${Number(id)}`;
      console.log("UPDATE request URL:", url);

      // Helper function to format dates to ISO format
      const formatDateToISO = (
        dateValue: string | Date | null,
        fieldName: string
      ): string | null => {
        if (!dateValue || dateValue === "N/A") {
          return null;
        }

        try {
          // Use local date parts to avoid UTC day-shift from toISOString
          const toLocalDateString = (d: Date) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

          // If it's already a Date object
          if (dateValue instanceof Date) {
            return toLocalDateString(dateValue);
          }

          // Try to create a Date object
          const dateObj = new Date(dateValue);

          // Check if the date is valid
          if (!isNaN(dateObj.getTime())) {
            return toLocalDateString(dateObj);
          }

          // Try parsing mm/dd/yyyy format
          if (typeof dateValue === "string") {
            const dateParts = dateValue.split("/");
            if (dateParts.length === 3) {
              const month = parseInt(dateParts[0], 10) - 1; // Months are 0-indexed in JS
              const day = parseInt(dateParts[1], 10);
              const year = parseInt(dateParts[2], 10);

              const parsedDate = new Date(year, month, day);
              if (!isNaN(parsedDate.getTime())) {
                return toLocalDateString(parsedDate);
              }
            }
          }

          console.log(`Could not parse ${fieldName} date:`, dateValue);
          return null;
        } catch (e) {
          console.error(`Error formatting ${fieldName} date:`, e);
          return null;
        }
      };

      // Format the dates properly to ISO format
      let formattedGraduationDate = formatDateToISO(
        employee.graduationDate,
        "graduation"
      );
      let formattedHireDate = formatDateToISO(employee.hireDate, "hire");

      // Prepare request body according to the API schema
      const requestBody = {
        First_Name: employee.firstName,
        Last_Name: employee.lastName,
        GPID: employee.gpid,
        Status: employee.status,
        Job_Description: employee.jobDescription,
        Manager: employee.manager || "",
        Peer_Coach: "", // Optional field, can be populated if available in the employee object
        Expected_Graduation_Date: formattedGraduationDate || null,
        Hire_Date: formattedHireDate || null, // Adding Hire_Date field with the formatted date
        location: selectedLocation.displayText || "", // Assuming location is required
      };

      // Make PUT API call to update the employee
      const response = await apiClient(api, url, "PUT", requestBody);

      // Parse the response according to the API schema
      // The response contains 'message' and 'updatedEmployee'
      const updatedEmployeeFromApi = response.updatedEmployee;

      // Transform the API response to match our Employee type
      const updatedEmployee: Employee = {
        ...employee, // Keep any fields not returned by the API
        firstName: updatedEmployeeFromApi.First_Name,
        lastName: updatedEmployeeFromApi.Last_Name,
        gpid: updatedEmployeeFromApi.GPID,
        status: updatedEmployeeFromApi.Status,
        jobDescription: updatedEmployeeFromApi.Job_Description,
        manager: updatedEmployeeFromApi.Manager,
        graduationDate: updatedEmployeeFromApi.Expected_Graduation_Date,
        hireDate: updatedEmployeeFromApi.Hire_Date || employee.hireDate, // Include Hire_Date from API response
      };

      // Return success response with the updated employee data from API
      return {
        success: true,
        employee: updatedEmployee,
        message: response.message || "Employee updated successfully.",
      };
    } catch (error: any) {
      try {
        const centralizedError = handleEmployeeUpdateErrorWithFriendlyMessage(
          EMPLOYEE_UPDATE_API_NAMES.UPDATE,
          error,
          true,
          { employeeId: employee?.id }
        );

        return rejectWithValue({
          success: false,
          message: centralizedError.message || 'Failed to update employee',
        });
      } catch (handlerErr) {
        console.error('Failed to dispatch centralized employee update error handler:', handlerErr);
        let errorMessage = 'Failed to update employee';
        if (typeof error === 'string') errorMessage = error;
        else if (error instanceof Error) errorMessage = error.message;
        else if (error?.response?.data?.message) errorMessage = error.response.data.message;
        return rejectWithValue({ success: false, message: errorMessage });
      }
    }
  }
);

export type Employee = {
  id: number;
  firstName: string;
  lastName: string;
  gpid: string;
  status: string;
  jobDescription: string;
  lastObservation: string;
  lastPulseCheck: string;
  hireDate: string;
  graduationDate: string;
  terminationDate: string;
  manager?: string;
  newHire: boolean;
  isDeleted?: boolean;
};

interface EmployeeUpdatesState {
  firstName: string;
  lastName: string;
  gpid: string;
  manager: string;
  jobDescription: string;
  selectedStatus: any; // This should match what's in your dropdown
  employeeList: Employee[];
  showPeriodObservation: boolean;
  showWeeklyPulseCheck: boolean;
  editEmployee: Employee | null;
  showOverlay: boolean;
  successMessage: string;
  filteredEmployeeList: Employee[];
  activeFilters: {
    [key: string]: string | null;
  };
  loading: boolean;
  error: string | null;
  gpidLookupLoading: boolean;
  gpidLookupError: string | null;
  isNameFromGpidLookup: boolean; // Flag to track if names were populated from GPID lookup
  validationErrors: {
    firstName?: string;
    lastName?: string;
    gpid?: string;
    manager?: string;
    jobDescription?: string;
    selectedStatus?: string;
    location?: string;
    [key: string]: string | undefined;
  };
  // Centralized errors for Employee Updates module (mirror ActionPlan pattern)
  centralizedErrors?: {
    errors: EmployeeUpdateError[];
    activeError: EmployeeUpdateError | null;
    isErrorModalVisible: boolean;
  };
}

const initialState: EmployeeUpdatesState = {
  firstName: "",
  lastName: "",
  gpid: "",
  manager: "",
  jobDescription: "",
  selectedStatus: null,
  validationErrors: {},
  employeeList: [
    {
      id: 1,
      firstName: "Abdo",
      lastName: "Zakkak",
      gpid: "80205774",
      status: "Inactive",
      jobDescription: "Forklift Operator",
      lastObservation: "01/01/2000",
      lastPulseCheck: "29/10/2024",
      hireDate: "N/A",
      graduationDate: "N/A",
      terminationDate: "N/A",
      newHire: false,
      isDeleted: false,
    },
    {
      id: 2,
      firstName: "Abishek",
      lastName: "Baskar",
      gpid: "81148100",
      status: "Active",
      jobDescription: "Inventory",
      lastObservation: "12/11/2024",
      lastPulseCheck: "03/12/2024",
      hireDate: "N/A",
      graduationDate: "N/A",
      terminationDate: "N/A",
      newHire: false,
      isDeleted: false,
    },
    {
      id: 3,
      firstName: "Absalom",
      lastName: "Rivas",
      gpid: "80947319",
      status: "Active",
      jobDescription: "Warehouse Person",
      lastObservation: "13/11/2024",
      lastPulseCheck: "09/12/2024",
      hireDate: "N/A",
      graduationDate: "N/A",
      terminationDate: "N/A",
      newHire: false,
      isDeleted: false,
    },
    {
      id: 4,
      firstName: "Adrian",
      lastName: "Custodio",
      gpid: "80863981",
      status: "Active",
      jobDescription: "Warehouse Person",
      lastObservation: "09/12/2024",
      lastPulseCheck: "10/12/2024",
      hireDate: "N/A",
      graduationDate: "N/A",
      terminationDate: "N/A",
      newHire: false,
      isDeleted: false,
    },
    {
      id: 5,
      firstName: "Ahmed",
      lastName: "Mohamed",
      gpid: "80903372",
      status: "Active",
      jobDescription: "Warehouse Person",
      lastObservation: "07/11/2024",
      lastPulseCheck: "10/12/2024",
      hireDate: "N/A",
      graduationDate: "N/A",
      terminationDate: "N/A",
      newHire: false,
      isDeleted: false,
    },
    {
      id: 6,
      firstName: "Alexander",
      lastName: "De Los Reyes",
      gpid: "10182447",
      status: "Active",
      jobDescription: "General Labor 1",
      lastObservation: "08/11/2024",
      lastPulseCheck: "18/11/2024",
      hireDate: "N/A",
      graduationDate: "N/A",
      terminationDate: "N/A",
      newHire: false,
      isDeleted: false,
    },
    {
      id: 7,
      firstName: "Alexis",
      lastName: "Dieguez",
      gpid: "80258180",
      status: "Active",
      jobDescription: "Warehouse Person",
      lastObservation: "04/12/2024",
      lastPulseCheck: "08/12/2024",
      hireDate: "N/A",
      graduationDate: "N/A",
      terminationDate: "N/A",
      newHire: false,
      isDeleted: false,
    },
    {
      id: 8,
      firstName: "Alfredo",
      lastName: "Franco",
      gpid: "10114300",
      status: "Inactive",
      jobDescription: "Certifier",
      lastObservation: "06/12/2024",
      lastPulseCheck: "09/12/2024",
      hireDate: "N/A",
      graduationDate: "N/A",
      terminationDate: "N/A",
      newHire: false,
      isDeleted: false,
    },
  ],
  showPeriodObservation: false,
  showWeeklyPulseCheck: false,
  editEmployee: null,
  showOverlay: false,
  successMessage: "",
  filteredEmployeeList: [],
  activeFilters: {
    fullName: null,
    gpid: null,
    status: null,
    jobDescription: null,
    lastObservation: null,
    lastPulseCheck: null,
    hireDate: null,
    terminationDate: null,
    newHire: null,
  },
  loading: false,
  error: null,
  gpidLookupLoading: false,
  gpidLookupError: null,
  isNameFromGpidLookup: false,
  // Initialize centralized errors
  centralizedErrors: {
    errors: [],
    activeError: null,
    isErrorModalVisible: false,
  },
};

export const employeeUpdatesSlice = createSlice({
  name: "employeeUpdates",
  initialState,
  reducers: {
    setFirstName: (state, action: PayloadAction<string>) => {
      state.firstName = action.payload;
      // Reset the flag when names are manually changed
      state.isNameFromGpidLookup = false;
    },
    setLastName: (state, action: PayloadAction<string>) => {
      state.lastName = action.payload;
      // Reset the flag when names are manually changed
      state.isNameFromGpidLookup = false;
    },
    setGpid: (state, action: PayloadAction<string>) => {
      state.gpid = action.payload;
      
      // If names were from GPID lookup, clear them when GPID is changed
      if (state.isNameFromGpidLookup) {
        state.firstName = "";
        state.lastName = "";
        state.isNameFromGpidLookup = false;
        
        // Clear validation errors for first name and last name since they're being cleared
        if (state.validationErrors.firstName) {
          delete state.validationErrors.firstName;
        }
        if (state.validationErrors.lastName) {
          delete state.validationErrors.lastName;
        }
      }
    },
    setManager: (state, action: PayloadAction<string>) => {
      state.manager = action.payload;
    },
    setJobDescription: (state, action: PayloadAction<string>) => {
      state.jobDescription = action.payload;
    },
    setSelectedStatus: (state, action: PayloadAction<any>) => {
      state.selectedStatus = action.payload;
    },
    setShowPeriodObservation: (state, action: PayloadAction<boolean>) => {
      state.showPeriodObservation = action.payload;
    },
    setShowWeeklyPulseCheck: (state, action: PayloadAction<boolean>) => {
      state.showWeeklyPulseCheck = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Mark employee as deleted locally (synchronous action)
    markEmployeeAsDeleted: (state, action: PayloadAction<number>) => {
      state.employeeList = state.employeeList.map((emp) =>
        emp.id === action.payload ? { ...emp, isDeleted: true } : emp
      );
      // Update filtered list
      state.filteredEmployeeList = state.filteredEmployeeList.filter(
        (emp) => emp.id !== action.payload || !emp.isDeleted
      );
    },
    setEditEmployee: (state, action: PayloadAction<Employee | null>) => {
      if (!action.payload) {
        state.editEmployee = null;
        return;
      }

      try {
        // Extract only the needed fields to avoid circular references
        // Ensure id is explicitly cast to a number
        const sanitizedEmployee: Employee = {
          id: Number(action.payload.id),
          firstName: action.payload.firstName || "",
          lastName: action.payload.lastName || "",
          gpid: action.payload.gpid || "",
          status: action.payload.status || "",
          jobDescription: action.payload.jobDescription || "",
          lastObservation: action.payload.lastObservation || "",
          lastPulseCheck: action.payload.lastPulseCheck || "",
          hireDate: action.payload.hireDate || "",
          graduationDate: action.payload.graduationDate || "",
          terminationDate: action.payload.terminationDate || "",
          manager: action.payload.manager || "",
          newHire:
            typeof action.payload.newHire === "string"
              ? action.payload.newHire === "●"
              : !!action.payload.newHire,
          isDeleted: action.payload.isDeleted || false,
        };

        state.editEmployee = sanitizedEmployee;
      } catch (error) {
        console.error("Error in setEditEmployee:", error);
        state.editEmployee = null;
      }
    },
    resetEmployeeDates: (state) => {
      // Reset both date fields at once if editEmployee exists
      if (state.editEmployee) {
        state.editEmployee = {
          ...state.editEmployee,
          hireDate: "",
          graduationDate: "",
        };
      }
    },
    setShowOverlay: (state, action: PayloadAction<boolean>) => {
      state.showOverlay = action.payload;
    },
    saveEditedEmployee: (state) => {
      if (!state.editEmployee) {
        return;
      }

      try {
        // Make a deep copy of the edit employee to prevent reference issues
        const updatedEmployee = JSON.parse(JSON.stringify(state.editEmployee));

        // Validate required fields
        if (
          !updatedEmployee.firstName ||
          !updatedEmployee.lastName ||
          !updatedEmployee.gpid
        ) {
          console.error("Missing required fields");
          state.successMessage = "Error: Required fields are missing";
          return;
        }

        // Update in main list
        state.employeeList = state.employeeList.map((emp) =>
          emp.id === updatedEmployee.id ? updatedEmployee : emp
        );

        // Update filtered list if it exists
        if (
          state.filteredEmployeeList &&
          state.filteredEmployeeList.length > 0
        ) {
          // Check if the employee exists in the filtered list
          const employeeInFilteredList = state.filteredEmployeeList.some(
            (emp) => emp.id === updatedEmployee.id
          );

          if (employeeInFilteredList) {
            // Update if it exists
            state.filteredEmployeeList = state.filteredEmployeeList.map((emp) =>
              emp.id === updatedEmployee.id ? updatedEmployee : emp
            );
          } else {
            // Force a refresh of the filtered list
            console.log("Employee not in filtered list, refreshing...");
            // This will be handled by the updateFilteredEmployeeList action
          }
        }

        // Set success message
        state.successMessage = "Employee updated successfully!";
      } catch (error) {
        console.error("Error updating employee:", error);
        state.successMessage = "Error updating employee record";
      } finally {
        // Always close the overlay
        state.showOverlay = false;
      }
    },
    addEmployee: (state) => {
      // Create a new employee with form data
      const newEmployee: Employee = {
        id: Math.max(...state.employeeList.map((emp) => emp.id)) + 1, // Generate a new ID
        firstName: state.firstName,
        lastName: state.lastName,
        gpid: state.gpid,
        status: state.selectedStatus?.displayText || "Pending",
        jobDescription: state.jobDescription,
        manager: state.manager,
        lastObservation: "N/A",
        lastPulseCheck: "N/A",
        hireDate: "N/A",
        graduationDate: "N/A",
        terminationDate: "N/A",
        newHire: true,
        isDeleted: false,
      };

      // Add to employee list
      state.employeeList.push(newEmployee);

      // Update filtered list if needed
      const matchesFilters = !Object.entries(state.activeFilters).some(
        ([key, value]) => {
          if (!value) {
            return false;
          } // No filter for this key

          switch (key) {
            case "fullName":
              const fullName =
                `${newEmployee.firstName} ${newEmployee.lastName}`.toLowerCase();
              return !fullName.includes(value.toLowerCase());
            case "gpid":
              return value !== "All" && !newEmployee.gpid.includes(value);
            case "status":
              return (
                value !== "All" &&
                newEmployee.status.toLowerCase() !== value.toLowerCase()
              );
            case "jobDescription":
              return (
                value !== "All" &&
                !newEmployee.jobDescription
                  .toLowerCase()
                  .includes(value.toLowerCase())
              );
            case "newHire":
              return (
                (value === "Yes" && !newEmployee.newHire) ||
                (value === "No" && newEmployee.newHire)
              );
            default:
              return false;
          }
        }
      );

      if (matchesFilters) {
        state.filteredEmployeeList.push(newEmployee);
      }

      // Clear form fields
      state.firstName = "";
      state.lastName = "";
      state.gpid = "";
      state.manager = "";
      state.jobDescription = "";
      state.selectedStatus = null;
    },
    updateFilteredEmployeeList: (state) => {
      if (!state.employeeList || state.employeeList.length === 0) {
        state.filteredEmployeeList = [];
        return;
      }

      // Check if status filter is applied and if it's "Terminated"
      const statusFilter = state.activeFilters.status;
      const isTerminatedFilterActive = statusFilter && typeof statusFilter === 'string' && statusFilter.toLowerCase() === "terminated";

      // Start with all non-deleted employees
      // Only exclude terminated employees if no status filter is applied or if the status filter is not "Terminated"
      let filtered = state.employeeList.filter((emp) => {
        if (emp.isDeleted) return false;
        
        // If terminated filter is active, include terminated employees
        if (isTerminatedFilterActive) {
          return true;
        }
        
        // Otherwise, exclude terminated employees by default
        return emp.status !== "Terminated";
      });

      // Apply fullName filter if present
      const fullNameFilter = state.activeFilters.fullName;
      if (fullNameFilter && typeof fullNameFilter === 'string') {
        filtered = filtered.filter((emp) => {
          const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
          return fullName.includes(fullNameFilter.toLowerCase());
        });
      }

      // Apply GPID filter if present
      const gpidFilter = state.activeFilters.gpid;
      if (gpidFilter && typeof gpidFilter === 'string' && gpidFilter !== "All") {
        filtered = filtered.filter((emp) => emp.gpid.includes(gpidFilter));
      }

      // Apply status filter if present
      if (statusFilter && typeof statusFilter === 'string' && statusFilter !== "All") {
        filtered = filtered.filter(
          (emp) => emp.status.toLowerCase() === statusFilter.toLowerCase()
        );
      }

      // Apply job description filter if present
      const jobDescFilter = state.activeFilters.jobDescription;
      if (jobDescFilter && typeof jobDescFilter === 'string' && jobDescFilter !== "All") {
        filtered = filtered.filter((emp) =>
          emp.jobDescription.toLowerCase().includes(jobDescFilter.toLowerCase())
        );
      }

      // Apply new hire filter if present
      const newHireFilter = state.activeFilters.newHire;
      if (newHireFilter) {
        filtered = filtered.filter((emp) => {
          return newHireFilter === "Yes" ? emp.newHire : !emp.newHire;
        });
      }

      state.filteredEmployeeList = filtered;
    },
    setActiveFilter: (
      state,
      action: PayloadAction<{ key: string; value: string | null }>
    ) => {
      const { key, value } = action.payload;
      state.activeFilters = {
        ...state.activeFilters,
        [key]: value,
      };
    },
    clearSuccessMessage: (state) => {
      state.successMessage = "";
    },
    updateEmployeeField: (
      state,
      action: PayloadAction<{ id: number; field: keyof Employee; value: any }>
    ) => {
      const { id, field, value } = action.payload;
      const empIdx = state.employeeList.findIndex((emp) => emp.id === id);
      if (empIdx !== -1) {
        (state.employeeList[empIdx] as any)[field] = value;
      }
      // Also update filteredEmployeeList if present
      const filteredIdx = state.filteredEmployeeList.findIndex(
        (emp) => emp.id === id
      );
      if (filteredIdx !== -1) {
        (state.filteredEmployeeList[filteredIdx] as any)[field] = value;
      }
    },

    // Validation actions
    setValidationError: (
      state,
      action: PayloadAction<{ field: string; message: string }>
    ) => {
      const { field, message } = action.payload;
      state.validationErrors = {
        ...state.validationErrors,
        [field]: message,
      };
    },

    clearValidationError: (state, action: PayloadAction<string>) => {
      const field = action.payload;
      if (state.validationErrors && state.validationErrors[field]) {
        const newErrors = { ...state.validationErrors };
        delete newErrors[field];
        state.validationErrors = newErrors;
      }
    },

    clearValidationErrors: (state) => {
      state.validationErrors = {};
    },

    // Set employee data from API response
    setEmployeeDataFromApi: (
      state,
      action: PayloadAction<{
        firstName: string;
        lastName: string;
        gpid: string;
        jobDescription: string;
        manager: string;
      }>
    ) => {
      state.firstName = action.payload.firstName;
      state.lastName = action.payload.lastName;
      state.gpid = action.payload.gpid;
      state.jobDescription = action.payload.jobDescription;
      state.manager = action.payload.manager;
    },

    // Reset employee data
    resetEmployeeData: (state) => {
      state.firstName = "";
      state.lastName = "";
      state.gpid = "";
      state.jobDescription = "";
      state.manager = "";
      state.isNameFromGpidLookup = false; // Reset the GPID lookup flag
    },

    // Centralized error reducers for Employee Updates
    addCentralizedError: (state, action: PayloadAction<EmployeeUpdateError>) => {
      state.centralizedErrors = state.centralizedErrors || { errors: [], activeError: null, isErrorModalVisible: false };
      state.centralizedErrors.errors.push(action.payload);
      if (state.centralizedErrors.errors.length > 10) {
        state.centralizedErrors.errors = state.centralizedErrors.errors.slice(-10);
      }
    },
    showErrorModal: (state, action: PayloadAction<EmployeeUpdateError>) => {
      state.centralizedErrors = state.centralizedErrors || { errors: [], activeError: null, isErrorModalVisible: false };
      state.centralizedErrors.activeError = action.payload;
      state.centralizedErrors.isErrorModalVisible = true;
      state.centralizedErrors.errors.push(action.payload);
      if (state.centralizedErrors.errors.length > 10) {
        state.centralizedErrors.errors = state.centralizedErrors.errors.slice(-10);
      }
    },
    hideErrorModal: (state) => {
      if (state.centralizedErrors) {
        state.centralizedErrors.isErrorModalVisible = false;
        state.centralizedErrors.activeError = null;
      }
    },
    setActiveError: (state, action: PayloadAction<EmployeeUpdateError | null>) => {
      state.centralizedErrors = state.centralizedErrors || { errors: [], activeError: null, isErrorModalVisible: false };
      state.centralizedErrors.activeError = action.payload;
    },
    clearAllErrors: (state) => {
      state.centralizedErrors = { errors: [], activeError: null, isErrorModalVisible: false };
    },
    removeErrorById: (state, action: PayloadAction<string>) => {
      if (!state.centralizedErrors) return;
      state.centralizedErrors.errors = state.centralizedErrors.errors.filter((e) => e.id !== action.payload);
      if (state.centralizedErrors.activeError?.id === action.payload) {
        state.centralizedErrors.activeError = null;
        state.centralizedErrors.isErrorModalVisible = false;
      }
    },
  },
  extraReducers: (builder) => {
    // Handle fetchEmployees async thunk
    builder
      .addCase(fetchEmployees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.employeeList = action.payload;
        // Reset filtered list to be same as employee list initially
        state.filteredEmployeeList = [...action.payload];
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to fetch employees";
      });

    // Handle createEmployee async thunk
    builder
      .addCase(createEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEmployee.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        // Handle the API response which includes message and newEmployee
        const response = action.payload;

        if (response && response.newEmployee) {
          // Create new employee object from API response
          const newEmployee: Employee = {
            id: Math.max(...state.employeeList.map((emp) => emp.id), 0) + 1, // Generate new ID
            firstName: response.newEmployee.First_Name || "",
            lastName: response.newEmployee.Last_Name || "",
            gpid: response.newEmployee.GPID || "",
            status: response.newEmployee.Status || "",
            jobDescription: response.newEmployee.Job_Description || "",
            manager: response.newEmployee.Manager || "",
            lastObservation: "N/A",
            lastPulseCheck: "N/A",
            hireDate: response.newEmployee.Hire_Date || "N/A",
            graduationDate:
              response.newEmployee.Expected_Graduation_Date || "N/A",
            terminationDate: "N/A",
            newHire: true,
            isDeleted: false,
          };

          // Add to employee list
          state.employeeList.push(newEmployee);

          // Check if new employee should be in filtered list
          const matchesFilters = !Object.entries(state.activeFilters).some(
            ([key, value]) => {
              if (!value) {
                return false;
              } // No filter for this key

              switch (key) {
                case "fullName":
                  const fullName =
                    `${newEmployee.firstName} ${newEmployee.lastName}`.toLowerCase();
                  return !fullName.includes(value.toLowerCase());
                case "gpid":
                  return value !== "All" && !newEmployee.gpid.includes(value);
                case "status":
                  return (
                    value !== "All" &&
                    newEmployee.status.toLowerCase() !== value.toLowerCase()
                  );
                case "jobDescription":
                  return (
                    value !== "All" &&
                    !newEmployee.jobDescription
                      .toLowerCase()
                      .includes(value.toLowerCase())
                  );
                case "newHire":
                  return (
                    (value === "Yes" && !newEmployee.newHire) ||
                    (value === "No" && newEmployee.newHire)
                  );
                default:
                  return false;
              }
            }
          );

          if (matchesFilters) {
            state.filteredEmployeeList.push(newEmployee);
          }
        }

        // Clear form fields after successful creation
        state.firstName = "";
        state.lastName = "";
        state.gpid = "";
        state.manager = "";
        state.jobDescription = "";
        state.selectedStatus = null;
        state.validationErrors = {};

        // Set success message for the main screen with employee details if available
        if (response && response.newEmployee) {
          const emp = response.newEmployee;
          const name = `${emp.First_Name || ""} ${emp.Last_Name || ""}`.trim();
          state.successMessage = `Employee ${name} (${
            emp.GPID || ""
          }) added successfully!`;
        } else {
          state.successMessage = "Employee added successfully!";
        }
      })
      .addCase(createEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to create employee";
      });

    // Handle lookupEmployeeByGpid async thunk
    builder
      .addCase(lookupEmployeeByGpid.pending, (state) => {
        state.gpidLookupLoading = true;
        state.gpidLookupError = null;
        // Don't reset the flag here - keep names locked if they were from previous lookup
        // until we know the result of this lookup
      })
      .addCase(lookupEmployeeByGpid.fulfilled, (state, action) => {
        state.gpidLookupLoading = false;
        state.gpidLookupError = null;
        
        // If employee data is found, populate first name and last name
        if (action.payload) {
          // Handle different response structures
          const employeeData = action.payload.data || action.payload;
          if (employeeData.first_name) {
            state.firstName = employeeData.first_name;
            state.isNameFromGpidLookup = true; // Mark that first name came from GPID lookup
          }
          if (employeeData.last_name) {
            state.lastName = employeeData.last_name;
            state.isNameFromGpidLookup = true; // Mark that last name came from GPID lookup
          }
          if (employeeData.job_description) {
            state.jobDescription = employeeData.job_description;
          }
          if (employeeData.manager) {
            state.manager = employeeData.manager;
          }
        }
      })
      .addCase(lookupEmployeeByGpid.rejected, (state, action) => {
        state.gpidLookupLoading = false;
        state.isNameFromGpidLookup = false; // Reset flag when lookup fails
        
        // Handle specific error messages for better user experience
        const errorMessage = action.payload as string;
        if (errorMessage === "Employee not found with this GPID" || errorMessage === "Employee not found for the provided GPID") {
          state.gpidLookupError = "No employee found with this GPID. You can continue entering the details manually.";
        } else {
          state.gpidLookupError = errorMessage || "Failed to lookup employee by GPID";
        }
        
        // Don't clear the form fields on lookup failure - let user continue manually
      });

    // Handle deleteEmployee async thunk
    builder
      .addCase(deleteEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEmployee.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        // Log the payload for debugging
        console.log("Delete employee fulfilled with payload:", action.payload);

        const id = action.payload.id;

        // Find the employee using the internal ID
        state.employeeList = state.employeeList.map((emp) =>
          emp.id === id ? { ...emp, isDeleted: true } : emp
        );

        // Update filtered list
        state.filteredEmployeeList = state.filteredEmployeeList.filter(
          (emp) => emp.id !== id || !emp.isDeleted
        );

        // Set success message
        state.successMessage =
          action.payload.message || "Employee deleted successfully";
      })
      .addCase(deleteEmployee.rejected, (state, action) => {
        state.loading = false;
        // Handle the rejected action payload which should contain a message
        const errorPayload = action.payload as { message: string } | undefined;
        state.error =
          errorPayload?.message ||
          action.error.message ||
          "Failed to delete employee";
      })

      // Handle updateEmployee async thunk
      .addCase(updateEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEmployee.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        // Log the payload for debugging
        console.log("Update employee fulfilled with payload:", action.payload);

        const updatedEmployee = action.payload.employee;

        if (updatedEmployee) {
          // First, find the employee in the list to preserve any fields not returned by the API
          const existingEmployeeIndex = state.employeeList.findIndex(
            (emp) => emp.id === updatedEmployee.id
          );

          if (existingEmployeeIndex !== -1) {
            // Update in main list with merged data
            state.employeeList[existingEmployeeIndex] = {
              ...state.employeeList[existingEmployeeIndex],
              ...updatedEmployee,
            };

            // Update in filtered list if employee exists
            if (
              state.filteredEmployeeList &&
              state.filteredEmployeeList.length > 0
            ) {
              const filteredIndex = state.filteredEmployeeList.findIndex(
                (emp) => emp.id === updatedEmployee.id
              );

              if (filteredIndex !== -1) {
                state.filteredEmployeeList[filteredIndex] = {
                  ...state.filteredEmployeeList[filteredIndex],
                  ...updatedEmployee,
                };
              }
            }
          }
        }

        // Set success message
        state.successMessage =
          action.payload.message || "Employee updated successfully";

        // Clear the edit employee since update was successful
        state.editEmployee = null;

        // Close the overlay
        state.showOverlay = false;
      })
      .addCase(updateEmployee.rejected, (state, action) => {
        state.loading = false;

        // Handle the rejected action payload which should contain a message
        const errorPayload = action.payload as { message: string } | undefined;
        state.error =
          errorPayload?.message ||
          action.error.message ||
          "Failed to update employee";
      });
  },
});

// Export actions
export const {
  setFirstName,
  setLastName,
  setGpid,
  setManager,
  setJobDescription,
  setSelectedStatus,
  setShowPeriodObservation,
  setShowWeeklyPulseCheck,
  clearError,
  markEmployeeAsDeleted,
  setEditEmployee,
  resetEmployeeDates,
  setShowOverlay,
  saveEditedEmployee,
  addEmployee,
  updateFilteredEmployeeList,
  setActiveFilter,
  clearSuccessMessage,
  updateEmployeeField,
  setValidationError,
  clearValidationError,
  clearValidationErrors,
  setEmployeeDataFromApi,
  resetEmployeeData,
  addCentralizedError,
  showErrorModal,
  hideErrorModal,
  setActiveError,
  clearAllErrors,
  removeErrorById,
} = employeeUpdatesSlice.actions;

// Export reducer
export default employeeUpdatesSlice.reducer;
